import { Fragment, useEffect, useState } from "react"

import { createNew, section } from "./css"
import {
  createSubRegion,
  fetchRegion,
  removeRegionalFinance,
  updateRegionalFinance,
} from "api/regions"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"
import { useHistory, useParams } from "react-router"

import { ApiError } from "api/http/utils"
import AutoCompleteInput from "components/inputs/AutoComplete"
import Button from "ui/buttons/Button"
import { ReactComponent as CloseIcon } from "assets/close.svg"
import SubRegion from "./SubRegion"
import { WithAuthority } from "components/WithAuthority"
import { fetchUnassignedMembers } from "api/team"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

function Region() {
  const { tab } = useParams<{ id: string; tab: string }>()
  const history = useHistory()
  const { data: region } = useQuery(["region", tab], fetchRegion)
  const [open, setOpen] = useState(false)
  const { t } = useI18n()
  const [newSubRegion, setNewSubRegion] = useState<string>()
  const [error, setError] = useState<string[] | undefined>()
  const { hasAuthority } = useAuthenticated()
  const [focused, setFocused] = useState(false)

  const [value, setValue] = useState()
  const { data: unassigned } = useQuery(
    ["unassignedMembers"],
    fetchUnassignedMembers,
    { enabled: hasAuthority(["AUTH_VIEW_USER_UNASSIGNED"]) },
  )
  const onChange = (e: any) => {
    setValue(e.id)
  }
  const [create] = useMutation(createSubRegion, {
    onSuccess: () => {
      queryCache.invalidateQueries("region")
      history.push(`/admin-regions/${tab}`)
      setOpen(false)
      setError(undefined)
    },
    onError: (error: ApiError) => {
      queryCache.invalidateQueries("region")
      setError(error.fields?.map((e: any) => e.key))
    },
  })
  const [openRF, setOpenRF] = useState<Boolean>(true)

  const [update] = useMutation(updateRegionalFinance, {
    onSuccess: () => {
      queryCache.invalidateQueries("region")
      queryCache.invalidateQueries("unassignedMembers")
      setOpenRF(true)
    },
  })

  const [remove] = useMutation(removeRegionalFinance, {
    onSuccess: async () => {
      queryCache.invalidateQueries("region")
      queryCache.invalidateQueries("unassignedMembers")
      setOpenRF(false)
    },
  })

  useEffect(() => {
    setOpen(false)
    setOpenRF(Boolean(region?.regionalFinance))
    setError(undefined)
    setNewSubRegion("")
  }, [tab, region])

  const handleBlur = () => {
    if (value) {
      region && update({ code: region.code, id: value })
    } else if (region?.regionalFinance) {
      setOpenRF(true)
    }
  }

  const hasSubRegions = region && region?.subRegions.length > 0

  return (
    <>
      {region && (
        <div className={section}>
          <div>
            <strong>{"Regional Finance"}</strong>
          </div>
          <WithAuthority allowed={["AUTH_MANAGE_USER_GLOBAL"]}>
            <>
              {!openRF && (
                <div className={autoComplete}>
                  <AutoCompleteInput<any>
                    size="medium"
                    margin="normal"
                    options={unassigned}
                    fetchOnFilter={false}
                    disableClearable={true}
                    label={"Regional Finance"}
                    onChange={(e) => onChange(e)}
                    onBlur={handleBlur}
                    getOptionLabel={(option) =>
                      option.name && `${option.name} `
                    }
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(option) => (
                      <Fragment>
                        <div>
                          <span>{`${option.firstname} ${option.lastname}`}</span>
                        </div>
                      </Fragment>
                    )}
                    disabled={!hasAuthority(["AUTH_MANAGE_USER_GLOBAL"])}
                  />
                </div>
              )}
            </>
          </WithAuthority>
          {openRF ? (
            <div className={regionalFinanceBox}>
              <div>
                <div className={rfName}>{region?.regionalFinance?.name}</div>
                <div className={rfEmail}>{region?.regionalFinance?.mail}</div>
                <div className={rfTitle}>{"Title"}</div>
                <div className={rfRegionFin}>{"Regional Finance"}</div>
              </div>
              {hasAuthority(["AUTH_MANAGE_USER_GLOBAL"]) && (
                <div>
                  <CloseIcon
                    className={closeicon}
                    onClick={() =>
                      region.regionalFinance &&
                      remove({ code: tab, id: region.regionalFinance.id })
                    }
                  />
                </div>
              )}
            </div>
          ) : (
            !hasAuthority(["AUTH_MANAGE_USER_GLOBAL"]) && (
              <div className={emptyRegional}>{t("No Regional Finance")}</div>
            )
          )}
        </div>
      )}
      <div className={section}>
        <>
          <div>
            <strong> {"Sub-Regions"}</strong>
            {hasSubRegions && (
              <WithAuthority allowed={["AUTH_CREATE_SUBREGION"]}>
                <strong className={createNew} onClick={() => setOpen(true)}>
                  {"+ New Sub-Region"}
                </strong>
              </WithAuthority>
            )}
          </div>
          {open && hasAuthority(["AUTH_CREATE_SUBREGION"]) && (
            <>
              <div className={cx(createSubRg, { [createSubRgOpen]: open })}>
                {!focused && "+"}
                <input
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  value={newSubRegion}
                  onChange={(e) => setNewSubRegion(e.currentTarget.value)}
                  placeholder={"Add name..."}
                  className={inputClass}
                  maxLength={64}
                />
                <Button
                  className={createButton}
                  variant="outlined"
                  onClick={() => {
                    setOpen(false)
                    setNewSubRegion("")
                    setError(undefined)
                  }}
                  color="primary"
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  className={createButton}
                  onClick={() =>
                    newSubRegion &&
                    create({ name: newSubRegion, regionCode: tab })
                  }
                  variant="contained"
                  color="primary"
                  disabled={newSubRegion === "" || newSubRegion === undefined}
                >
                  {t("actions.create")}
                </Button>
              </div>
              <div className={errorMessage}>
                {error ? error.map((error: any) => t(error)) : ""}
              </div>
            </>
          )}
        </>

        <WithAuthority allowed={["AUTH_VIEW_SUBREGION"]}>
          <>
            {!open && !hasSubRegions && (
              <>
                <div className={emptyMessage}>{t("subregion.empty.list")}</div>
                <WithAuthority allowed={["AUTH_CREATE_SUBREGION"]}>
                  <div onClick={() => setOpen(true)} className={emptySubregion}>
                    {t("subregion.empty.list.add")}
                  </div>
                </WithAuthority>
              </>
            )}
            <div className={cx("BodyBody-2Dark-greyRegular", header)}>
              <span>Sub Region</span>
              <div>
                <span
                  className={cx(headerMarket, {
                    [financeHeaderMarket]: !hasAuthority(["AUTH_EDIT_MARKET"]),
                  })}
                >
                  Markets
                </span>
                {hasAuthority(["AUTH_EDIT_MARKET"]) && <span>Actions</span>}
              </div>
            </div>
            {region &&
              region.subRegions.map((subRegion: any, idx: number) => (
                <SubRegion key={idx} subRegion={subRegion} />
              ))}
          </>
        </WithAuthority>
      </div>
    </>
  )
}

export default Region

const createSubRg = css`
  align-items: center;
  border: dashed 1px var(--primary-main);
  height: 46px;
  padding: 11px 39px 11px 14px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: var(--dark-grey);
  width: 989px;
  display: none;
`
const inputClass = css`
  outline: none;
  border: none;
  width: 989px;
`

const createSubRgOpen = css`
  display: flex;
`
const createButton = css`
  margin-left: var(--spacing-xs);
  padding: 5px 10px 5px 10px;
  width: 80px;
`

const errorMessage = css`
  font-size: 13px;
  color: var(--light-red);
`
const regionalFinanceBox = css`
  display: flex;
  justify-content: space-between;
  width: 309px;
  height: 107px;
  margin: 24px 524px 40px 0;
  padding: 15px 16px 16px;
  border-radius: 3px;
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.16);
  border: solid 1px var(--light-grey);
  background-color: var(--white);
`
const rfName = css`
  font-size: 14px;
  color: var(--black);
`
const rfEmail = css`
  font-size: 13px;
  color: #00b1a3;
  margin-bottom: 12px;
`
const rfTitle = css`
  font-size: 12px;
  font-weight: 300;
  color: var(--black);
`
const rfRegionFin = css`
  font-size: 12px;
  font-weight: 300;
  color: var(--dark-grey);
`
const closeicon = css`
  width: 11px;
  height: 11px;
  cursor: pointer;
`
const autoComplete = css`
  width: 255px;
`
const header = css`
  display: flex;
  justify-content: space-between;
  padding: 11px 24px 0px 24px;
  width: 989px;
`
const emptyRegional = css`
  font-size: 16px;
  font-weight: 300;
  color: var(--dark-grey);
  margin-top: var(--spacing-m);
`
const financeHeaderMarket = css`
  margin-right: 147px;
`
const headerMarket = css`
  margin-right: 104px;
`

const emptyMessage = css`
  margin: 27px 0px 8px 0px;
  font-size: 16px;
  font-weight: 300;
  text-align: center;
  color: var(--dark-grey);
`

const emptySubregion = css`
  font-size: 13px;
  font-weight: bold;
  text-align: center;
  color: var(--primary-main);
  cursor: pointer;
`
